import { last, sortBy } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';

import { useLoggedInEmployeeId } from '~/components/Auth';
import {
  AppraisalTemplate,
  useGetEmployeeAppraisalTemplates,
} from '~/data/appraisal';
import { TabModules, useModulesConfig } from '~/data/company';
import { useGetEmployeePerformanceGoals } from '~/data/performance';
import { PerformanceTabs } from '~/data/tabs';
import {
  EvaluationSummaryDto,
  PerformanceGoalFullDto,
} from '~/services/Http/client.people.generated';
import { useGetAppraisalEvaluations } from '~/services/Http/hooks';
import { useGetEmployeePerformanceScores } from '~/services/Http/hooks/people/useGetEmployeeCurrentPerformanceScores';

export type MyPerformanceWidgetTableRow = {
  id: number;
  performance: PerformanceGoalFullDto;
  latestV2Score: number;
  appraisal: {
    name: string;
    status: string;
    lastUpdatedAt: string;
  };
};

export const useMyPerformanceWidget = () => {
  const { t } = useTranslation();

  const employeeId = useLoggedInEmployeeId();

  const { isModuleEnabled, isModuleTabEnabled } = useModulesConfig();

  const { isTabDisabled, isGoalsV2Enabled, isAppraisalsEnabled } =
    useMemo(() => {
      return {
        isTabDisabled: !isModuleEnabled(TabModules.PERFORMANCE),
        isGoalsV2Enabled: isModuleTabEnabled(
          TabModules.PERFORMANCE,
          PerformanceTabs.GoalEvaluation,
        ),
        isAppraisalsEnabled: isModuleTabEnabled(
          TabModules.PERFORMANCE,
          PerformanceTabs.AppraisalEvaluation,
        ),
      };
    }, [isModuleEnabled, isModuleTabEnabled]);

  const swrConfig: SWRConfiguration = { revalidateOnFocus: false };

  const {
    data: performanceScores,
    loading,
    failedToLoad,
  } = useGetEmployeePerformanceScores({
    employeeId: 'me',
  });

  const {
    appraisalEvaluations,
    loading: loadingAppraisals,
    failedToLoad: failedToLoadAppraisals,
  } = useGetAppraisalEvaluations(
    {
      isEnabled: isAppraisalsEnabled,
    },
    swrConfig,
  );

  const {
    appraisalTemplates,
    isLoading: isLoadingAppraisalTemplates,
    failedToLoad: failedToLoadAppraisalTemplates,
  } = useGetEmployeeAppraisalTemplates(
    { employeeId: isAppraisalsEnabled ? employeeId : undefined },
    swrConfig,
  );

  const {
    performanceGoals,
    isLoading: isLoadingPerformanceGoals,
    failedToLoad: failedToLoadPerformanceGoals,
  } = useGetEmployeePerformanceGoals(
    { employeeId: isGoalsV2Enabled ? employeeId : undefined },
    swrConfig,
  );

  const resolveLastAppraisalEvaluation = useCallback(
    (employeeId: number, appraisalTemplates: AppraisalTemplate[]) => {
      const resolveActiveAppraisalTemplates = (
        appraisalTemplates: AppraisalTemplate[],
      ) => {
        const activeStateTemplates = appraisalTemplates.filter(
          ({ status }) => status === 'ACTIVE',
        );

        const nonDeadlinedTemplates = activeStateTemplates.filter(
          ({ deadline }) => deadline > new Date().toISOString(),
        );

        return nonDeadlinedTemplates.length === 0
          ? activeStateTemplates
          : nonDeadlinedTemplates;
      };

      const resolveAppraisalEvaluations = (
        appraisalTemplates: AppraisalTemplate[],
        appraisalEvaluations: EvaluationSummaryDto[],
      ): {
        template: AppraisalTemplate | undefined;
        evaluation: EvaluationSummaryDto | undefined;
      } => {
        // No Templates
        if (appraisalTemplates.length === 0) {
          return {
            template: undefined,
            evaluation: undefined,
          };
        }

        const activeTemplateIds = appraisalTemplates.map(
          (template: { id: string }) => template.id,
        );
        const matchingEvaluations = appraisalEvaluations?.filter(
          (evaluation: EvaluationSummaryDto) =>
            evaluation.employee.id === employeeId &&
            activeTemplateIds.includes(evaluation.templateId),
        );

        // Has Evaluations
        if (matchingEvaluations.length > 0) {
          const lastEvaluation = last(
            sortBy(matchingEvaluations, (e) => e.lastUpdatedAt),
          );

          const template = appraisalTemplates.find(
            (t) => t.id === lastEvaluation?.templateId,
          );

          return {
            template,
            evaluation: lastEvaluation,
          };
        }

        // No Evaluations
        return {
          template: last(sortBy(appraisalTemplates, (t) => t.updatedTime)),
          evaluation: undefined,
        };
      };

      return resolveAppraisalEvaluations(
        resolveActiveAppraisalTemplates(appraisalTemplates),
        appraisalEvaluations,
      );
    },
    [appraisalEvaluations],
  );

  const statusTranslation = useCallback(
    (status: string) => {
      switch (status) {
        case 'NOT_STARTED':
          return t('notStarted');
        case 'IN_PROGRESS':
          return t('inProgress');
        case 'COMPLETED':
          return t('completed');
        case 'EMPLOYEE_APPROVED':
          return t('laEmployeeApproved');
        default:
          return status;
      }
    },
    [t],
  );

  const myPerformanceWidgetTableRows: MyPerformanceWidgetTableRow[] =
    useMemo(() => {
      const lastGoalsV2 = last(performanceGoals);

      const { template, evaluation } = resolveLastAppraisalEvaluation(
        employeeId,
        appraisalTemplates,
      );

      return !!performanceScores
        ? [
            {
              id: employeeId,
              performance: performanceScores,
              latestV2Score: lastGoalsV2?.totScore ?? 0,
              appraisal: {
                name: template?.subject ?? '',
                status: statusTranslation(evaluation?.status ?? 'NOT_STARTED'),
                lastUpdatedAt:
                  !!evaluation?.status && !!evaluation?.lastUpdatedAt
                    ? moment(evaluation.lastUpdatedAt).format('L LT')
                    : '',
              },
            },
          ]
        : [];
    }, [
      employeeId,
      performanceScores,
      performanceGoals,
      appraisalTemplates,
      resolveLastAppraisalEvaluation,
      statusTranslation,
    ]);

  return {
    tableRows: myPerformanceWidgetTableRows,
    loading:
      loading ||
      loadingAppraisals ||
      isLoadingAppraisalTemplates ||
      isLoadingPerformanceGoals,
    failedToLoad:
      failedToLoad ||
      failedToLoadAppraisals ||
      failedToLoadAppraisalTemplates ||
      failedToLoadPerformanceGoals,
    isTabDisabled,
  };
};
